import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import React from "react";
import Button from "react-bootstrap/Button";
import FormCheck from "react-bootstrap/FormCheck";
import Col from "react-bootstrap/Col";
import FormLabel from "react-bootstrap/FormLabel";
import FormControl from "react-bootstrap/FormControl";
import FormGroup from "react-bootstrap/FormGroup";
import FormText from "react-bootstrap/FormText";
import Row from "react-bootstrap/Row";
import { Link, Redirect } from "react-router-dom";
import Select from "react-select";
import { Creatable } from "react-select";
import * as Device from "redux-store/devices/selectors";
import { AdPlatformSyncedAlert } from "ui/alert/ad-platform-synced-alert";
import { BackButton } from "ui/button/back";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { OperationScheduleForm } from "ui/operation-schedule/operation-schedule-form";
import { getErrorMessage, getFormErrors } from "util/response";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

class EditDevicePage extends React.PureComponent {
  static get propTypes() {
    return {
      device: PropTypes.object,
      deviceTagOptions: PropTypes.array.isRequired,
      editDevice: PropTypes.func.isRequired,
      network: PropTypes.object,
      networkOptions: PropTypes.array.isRequired,
      readonly: PropTypes.bool.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      formErrors: {},
      errorMessage: "",
      networkId: null,
      hasOverrideNetwork: false,
      deviceOperateTime: "",
      selectedNetwork: null,
      selectedTags: null,
      submitted: false,
      successMessage: "",
      testing: false,
    };

    this._editAction = this._editAction.bind(this);
    this._onTagSelect = this._onTagSelect.bind(this);
    this._submit = this._submit.bind(this);
    this._updateDeviceOperateHours = this._updateDeviceOperateHours.bind(this);
    this._updateHasOverrideNetwork = this._updateHasOverrideNetwork.bind(this);
    this._updateLocation = this._updateLocation.bind(this);
    this._updateName = this._updateName.bind(this);
    this._updateNetwork = this._updateNetwork.bind(this);
    this._updateTesting = this._updateTesting.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this._shouldUpdateFormState(this.props, prevProps)) {
      this._updateFormState(this.props);
    }
  }

  componentDidMount() {
    this._updateFormState(this.props);
  }

  _updateFormState(props) {
    const { device, network } = props;

    if (!device || !network) {
      return;
    }

    const hasDeviceOperateTime = device.operate_time !== null;
    const deviceOperateTime = hasDeviceOperateTime
      ? device.operate_time
      : network.device_operate_time;
    const nextState = {
      deviceOperateTime,
      hasOverrideNetwork: hasDeviceOperateTime,
      selectedNetwork: { value: network.id, label: network.name },
      selectedTags: this._getSelectedDeviceTags(props),
      testing: device.testing,
    };

    this.setState(nextState);
  }

  _shouldUpdateFormState(newProps, oldProps) {
    return (
      !isEqual(newProps.device, oldProps.device) ||
      !isEqual(newProps.network, oldProps.network)
    );
  }

  _onTagSelect(value) {
    this.setState({ selectedTags: value });
  }

  _getSelectedDeviceTags(props) {
    const { device } = props;
    return Device.getDeviceTags(device);
  }

  _updateDeviceOperateHours(deviceOperateTime) {
    this.setState({ deviceOperateTime });
  }

  _updateHasOverrideNetwork(e) {
    const hasOverrideNetwork = e.currentTarget.checked;
    const { device, network } = this.props;
    let deviceOperateTime = network.device_operate_time;

    if (hasOverrideNetwork && device.operate_time !== null) {
      deviceOperateTime = device.operate_time;
    }

    this.setState({
      deviceOperateTime,
      hasOverrideNetwork,
    });
  }

  _updateName() {
    this.setState({ name: this.deviceName.value });
  }

  _updateNetwork(selected) {
    this.setState({ selectedNetwork: selected });
  }

  _updateTesting(e) {
    this.setState({ testing: e.currentTarget.checked });
  }

  _updateLocation(e) {
    this.setState({ location: this.deviceLocation.value });
  }

  _submit() {
    this.setState({ submitted: true }, this._editAction);
  }

  _editAction() {
    const { device, editDevice } = this.props;
    const {
      deviceOperateTime,
      hasOverrideNetwork,
      selectedNetwork,
      selectedTags,
      testing,
    } = this.state;
    const networkId = selectedNetwork ? selectedNetwork.value : null;
    const { id } = device;
    const tags = selectedTags.map((t) => t.label);
    const name = this.deviceName.value;
    const location = this.deviceLocation.value;

    const editArgs = {
      id,
      name,
      networkId,
      testing,
      location,
      tags,
      operateParams: hasOverrideNetwork ? deviceOperateTime : null,
    };

    return editDevice(editArgs)
      .then(() => {
        this.setState({
          ...getFormErrors(),
          errorMessage: "",
          successMessage: "Device details are updated.",
        });
      })
      .catch((e) => {
        this.setState({
          ...getFormErrors(e),
          errorMessage: getErrorMessage(e),
          successMessage: "",
        });
      })
      .finally(() => {
        this.setState({ submitted: false });
      });
  }

  _renderTestingCheckbox() {
    const { testing } = this.state;

    return (
      <FormGroup>
        <FormCheck
          checked={testing}
          label="Testing device"
          onChange={this._updateTesting}
          ref={(r) => {
            this.testing = r;
          }}
          type="checkbox"
        />
        <FormText>
          Checking this option will put the device in testing mode. When in
          testing mode, this device will get the latest testing versions of the
          applications. Non-testing mode devices cannot run the testing
          application versions.
        </FormText>
      </FormGroup>
    );
  }

  _renderOverrideCheckbox() {
    const { hasOverrideNetwork } = this.state;

    return (
      <FormGroup>
        <FormCheck
          checked={hasOverrideNetwork}
          label="Override Network’s Screen Operating Hours"
          onChange={this._updateHasOverrideNetwork}
          type="checkbox"
        />
        <FormText>
          By checking this box this device will override the network’s screen
          operating hours
        </FormText>
      </FormGroup>
    );
  }

  _renderView() {
    const {
      device,
      deviceTagOptions,
      network,
      networkOptions,
      readonly,
      storesLoaded,
    } = this.props;
    const {
      deviceOperateTime,
      formErrors,
      errorMessage,
      hasOverrideNetwork,
      selectedNetwork,
      selectedTags,
      submitted,
      successMessage,
    } = this.state;
    const systemManaged = Device.isSystemManaged(device);

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    if (!device) {
      return <Redirect to="/404" />;
    }

    if (!network) {
      return <Redirect to="/404" />;
    }

    return (
      <div>
        <Notification
          error={errorMessage}
          success={successMessage}
          successChildren={
            <Link to={`/devices/${device.device_id}`}>View Device</Link>
          }
        />
        <Row>
          <Col xs={4}>
            <FormGroup
              name="device-name"
            >
              <FormLabel>Device name</FormLabel>
              <FormControl
                ref={(r) => {
                  this.deviceName = r;
                }}
                type="text"
                defaultValue={device.name}
                disabled={systemManaged}
                onChange={this._updateName}
                placeholder="Device name"
                required={true}
                isInvalid={!!formErrors.name}
              />
              <FormText>
                Required. Name your device for future reference.
              </FormText>
            </FormGroup>
          </Col>
          <Col xs={4}>
            <FormGroup name="network">
              <FormLabel>Network</FormLabel>
              <Select
                name="network-select"
                isClearable={false}
                isDisabled={systemManaged}
                onChange={this._updateNetwork}
                options={networkOptions}
                value={selectedNetwork}
                required
              />
              <FormText>
                Required. Please select the network this device will be part of.
              </FormText>
            </FormGroup>
          </Col>
          <Col xs={4}>
            <FormGroup>
              <FormLabel>Device location (latitude, longitude)</FormLabel>
              <FormControl
                ref={(r) => {
                  this.deviceLocation = r;
                }}
                type="text"
                placeholder="latitude,longitude"
                defaultValue={device.location}
                onChange={this._updateLocation}
              />
              <FormText>
                Optional. GPS coordinates of this device. e.g.
                39.965033,-75.179170
              </FormText>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <FormGroup
              name="device-tags"
            >
              <FormLabel>Device Tags</FormLabel>
              <Creatable
                name="device-tags"
                promptTextCreator={(tag) => `Create tag "${tag}"`}
                onChange={this._onTagSelect}
                options={deviceTagOptions}
                value={selectedTags}
                isMulti
                className={formErrors.tags ? "is-invalid" : ""}
              />
              <FormText>
                Optional. Type one tag at a time and hit Enter. Tags are
                case-insensitive and have to start with alphanumeric characters.
                Tags can only contain alphanumeric characters, &quot;-&quot;,
                &quot;_&quot; or a space.
              </FormText>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>{this._renderTestingCheckbox()}</Col>
        </Row>
        <Row>
          <Col xs={12}>{this._renderOverrideCheckbox()}</Col>
        </Row>
        <Row>
          <Col xs={12}>
            <FormGroup controlId="deviceOperateSchedule">
              <OperationScheduleForm
                schedule={deviceOperateTime}
                onChange={this._updateDeviceOperateHours}
                readOnly={readonly || !hasOverrideNetwork}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <BackButton />
            <Button
              variant="primary"
              onClick={this._submit}
              disabled={submitted}
            >
              Update
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  render() {
    const { device } = this.props;
    const deviceName = device ? Device.getName(device) : "Device";
    const systemManaged = Device.isSystemManaged(device);

    const view = this._renderView();

    return (
      <Page title={`Edit ${deviceName}`}>
        {systemManaged && <AdPlatformSyncedAlert objectName="device" />}
        <Box noHeader={true}>
          <Row>
            <Col xs={12}>{view}</Col>
          </Row>
        </Box>
      </Page>
    );
  }
}

export { EditDevicePage };
